/**
 * Event lifecycle contracts (plan 2026-09-09-1309, U12 — R24 to R28, R48;
 * KTD11): cancel, archive and unarchive an event, and the admin list's archive
 * filter. Delete keeps its existing route; its refusal is the typed 409
 * `EVENT_HAS_REGISTRATIONS` (R27).
 *
 * Admin copy says "registration", never "admission" (KTD1).
 */

import { z } from 'zod'

// ============================================================================
// Cancel
// ============================================================================

/** Cancel counts as a cancellation in reports; void hides every registration from them (R6, R7). */
export const CancelEventVerbSchema = z.enum(['cancel', 'void'])
export type CancelEventVerb = z.infer<typeof CancelEventVerbSchema>

/**
 * What happens to each released seat (R15). `none` keeps it taken. The
 * waitlist `hold` is not offered on a cancelled event: nobody can claim a seat
 * at an event that will not happen.
 */
export const CancelEventDispositionSchema = z.enum(['return', 'none'])
export type CancelEventDisposition = z.infer<typeof CancelEventDispositionSchema>

/** POST /v1/ticketed-events/:eventPublicId/cancel */
export const CancelEventRequestSchema = z
  .object({
    /** Shown to registrants on the event page and in the notice. */
    reason: z.string().trim().min(1).max(1000),
    verb: CancelEventVerbSchema.default('cancel'),
    disposition: CancelEventDispositionSchema.default('return'),
    /** Email each distinct registrant once. A no-op until the school's event emails are set up. */
    notify: z.boolean().default(false)
  })
  .strict()
export type CancelEventRequest = z.input<typeof CancelEventRequestSchema>
export type CancelEventRequestParsed = z.infer<typeof CancelEventRequestSchema>

export const CancelEventResponseSchema = z.object({
  data: z.object({
    /**
     * True when the event was already cancelled before this call. The call
     * still swept whatever was left live, so a cancel that failed part-way is
     * finished by calling it again.
     */
    alreadyCancelled: z.boolean(),
    registrationsCancelled: z.number().int(),
    registrationsVoided: z.number().int(),
    /** Pending applications and unpaid checkout holds this call cancelled. */
    ordersCancelled: z.number().int(),
    /** Refund-owed records raised for paid registrations by this call. */
    refundsOwed: z.number().int(),
    /**
     * `not_configured` when no event-cancelled email is set up for the school;
     * otherwise how many distinct people a notice was queued for. Zero when
     * notify was off.
     */
    notified: z.union([z.literal('not_configured'), z.number().int()])
  })
})
export type CancelEventResponse = z.infer<typeof CancelEventResponseSchema>

// ============================================================================
// Archive
// ============================================================================

/** POST /v1/ticketed-events/:eventPublicId/{archive,unarchive} */
export const ArchiveEventResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    archivedAt: z.string().nullable(),
    /** True when the event was already in the requested state; nothing changed. */
    alreadyInState: z.boolean()
  })
})
export type ArchiveEventResponse = z.infer<typeof ArchiveEventResponseSchema>

/**
 * The admin list's archive filter (R28). `exclude` is the default working
 * view; `only` is the archive; `include` is everything.
 */
export const ArchivedEventsFilterSchema = z.enum(['exclude', 'include', 'only'])
export type ArchivedEventsFilter = z.infer<typeof ArchivedEventsFilterSchema>
